import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { parseUrl } from "./lib/archive/parseUrl.ts";
import { fetchMetadata, MetadataError } from "./lib/archive/metadata.ts";
import { buildItem } from "./lib/archive/buildItem.ts";
import {
  addItem as qAddItem,
  currentItem as qCurrentItem,
  emptyQueue,
  jumpTo as qJumpTo,
  next as qNext,
  prev as qPrev,
  removeAt as qRemoveAt,
  reorder as qReorder,
} from "./lib/storage/queue.ts";
import { installKeyboardHandlers } from "./lib/keyboard.ts";
import {
  setMediaSessionHandlers,
  setMediaSessionMetadata,
} from "./lib/player/mediaSession.ts";
import {
  defaultSettings,
  loadLibrary,
  saveLibrary,
  type LibrarySettings,
} from "./lib/storage/library.ts";
import type { CapabilityReport, DisplayPreferences, Item, Queue, TVLook } from "./types.ts";
import { defaultDisplayPreferences } from "./types.ts";
import { PasteBox } from "./components/PasteBox.tsx";
import { Player, type PlayerHandle } from "./components/Player.tsx";
import { Attribution } from "./components/Attribution.tsx";
import { Queue as QueueList } from "./components/Queue.tsx";
import { DonateFooter } from "./components/DonateFooter.tsx";
import { ShortcutsOverlay } from "./components/ShortcutsOverlay.tsx";
import { EmptyState } from "./components/EmptyState.tsx";

type Notice = { kind: "info" | "warning" | "error"; message: string } | null;

const DEMO_NAME = "Night Light Demo Channel";

export function App() {
  // Lazy-init from localStorage on first render. Falls back to an empty queue.
  const initial = useMemo(() => loadLibrary(), []);
  const [queue, setQueue] = useState<Queue>(() => initial?.queue ?? emptyQueue());
  const [settings, setSettings] = useState<LibrarySettings>(
    () => initial?.settings ?? defaultSettings,
  );
  const [loading, setLoading] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);
  const [shortcutsOpen, setShortcutsOpen] = useState(false);
  const playerRef = useRef<PlayerHandle>(null);

  // ----- CRT shader layer state -------------------------------------------
  const [crtPrefs, setCrtPrefs] = useState<DisplayPreferences>(
    () => (initial as { display?: DisplayPreferences } | null)?.display ?? defaultDisplayPreferences,
  );
  const [crtMounted, setCrtMounted] = useState(false);
  const [crtLoading, setCrtLoading] = useState(false);
  const [crtCanDrive, setCrtCanDrive] = useState(true);
  const [crtDisabledReason, setCrtDisabledReason] = useState<string | null>(null);
  const [glowStyle, setGlowStyle] = useState<React.CSSProperties | undefined>(undefined);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const shaderHandleRef = useRef<{ setLook: (l: TVLook) => void; setTier: (t: import("./types.ts").QualityTier) => void; unmount: () => void; isMounted: () => boolean } | null>(null);
  const capsRef = useRef<CapabilityReport | null>(null);

  const current: Item | null = useMemo(() => qCurrentItem(queue), [queue]);

  // Show the demo channel whenever the queue is empty AND the visitor has
  // never added anything of their own. If `userOwnsQueue` is true, an
  // empty queue means "visitor cleared it", not "first visit".
  const showDemo = queue.items.length === 0 && !settings.userOwnsQueue;

  // Debounced persistence. Save 250ms after the last mutation so
  // rapid changes (reorder, removeAt) coalesce. We don't persist a
  // demo queue — only real user channels survive the reload.
  useEffect(() => {
    if (!settings.userOwnsQueue && queue.items.length === 0) {
      return;
    }
    const handle = window.setTimeout(() => {
      saveLibrary({ queue, settings });
    }, 250);
    return () => window.clearTimeout(handle);
  }, [queue, settings]);

  // ----- paste flow --------------------------------------------------------

  const addByUrl = useCallback(async (raw: string) => {
    const id = parseUrl(raw);
    if (!id) {
      setNotice({
        kind: "warning",
        message:
          "That doesn't look like an archive.org URL. Try a link from archive.org/details/...",
      });
      return;
    }
    setNotice(null);
    setLoading(true);
    try {
      const meta = await fetchMetadata(id);
      const built = buildItem(meta);
      // First paste from the demo channel state: REPLACE the demo queue
      // with the visitor's item, then mark the queue user-owned so
      // subsequent pastes append and the demo never returns.
      // We key the "replace vs append" decision on the queue's NAME
      // rather than a captured settings value — useCallback's `[]`
      // deps would otherwise stale-close the settings flag on every
      // paste after the first.
      setQueue((q) => {
        const isDemo = q.name === DEMO_NAME;
        const base = isDemo ? emptyQueue() : q;
        return qAddItem(base, built);
      });
      setSettings((s) => (s.userOwnsQueue ? s : { ...s, userOwnsQueue: true }));
      if (built.isEmbedFallback) {
        setNotice({
          kind: "warning",
          message:
            "This item has no browser-playable MP4 derivative. Auto-advance won't work for it — open it on archive.org to watch.",
        });
      }
    } catch (err) {
      if (err instanceof MetadataError && err.kind === "collection") {
        setNotice({
          kind: "info",
          message:
            "That's a collection, not a single item — collection import is coming in a later release.",
        });
      } else if (err instanceof MetadataError && err.kind === "not-found") {
        setNotice({
          kind: "error",
          message: "Archive.org has no item with that ID.",
        });
      } else if (err instanceof MetadataError) {
        setNotice({
          kind: "error",
          message: `Couldn't load metadata: ${err.message}`,
        });
      } else {
        setNotice({
          kind: "error",
          message: `Something went wrong: ${(err as Error).message}`,
        });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  // ----- queue operations --------------------------------------------------

  const doJumpTo = useCallback((index: number) => setQueue((q) => qJumpTo(q, index)), []);
  const doRemove = useCallback((index: number) => setQueue((q) => qRemoveAt(q, index)), []);
  const doReorder = useCallback(
    (from: number, to: number) => setQueue((q) => qReorder(q, from, to)),
    [],
  );
  const doNext = useCallback(() => setQueue((q) => qNext(q)), []);
  const doPrev = useCallback(() => setQueue((q) => qPrev(q)), []);

  // ----- CRT toggle flow ---------------------------------------------------

  const handleCrtToggle = useCallback(async () => {
    if (crtMounted) {
      // Turn OFF
      shaderHandleRef.current?.unmount();
      shaderHandleRef.current = null;
      setCrtMounted(false);
      setGlowStyle(undefined);
      setCrtPrefs((p) => ({ ...p, crtEnabled: false }));
      return;
    }
    // Turn ON — lazy import + capability check + mount
    setCrtLoading(true);
    try {
      const shader = await import("./lib/shader/index.ts");
      if (!capsRef.current) {
        capsRef.current = await shader.capability();
      }
      const caps = capsRef.current;
      if (!caps.canDrive) {
        setCrtCanDrive(false);
        setCrtDisabledReason(
          "Your device can\u2019t sustain this feature without dropping video frames. Try on a more capable device.",
        );
        setCrtLoading(false);
        return;
      }
      const look = shader.resolveSelectedLook(crtPrefs, caps);
      const canvas = canvasRef.current;
      const video = canvas?.closest(".player-stage")?.querySelector<HTMLVideoElement>("video");
      if (!canvas || !video) {
        setCrtLoading(false);
        return;
      }
      const handle = shader.mount({
        canvas,
        video,
        initialLook: look,
        initialTier: caps.autoSelectedTier,
        onError: (err) => {
          if (err.kind === "cannot-drive" || err.kind === "context-restore-failed") {
            handle.unmount();
            shaderHandleRef.current = null;
            setCrtMounted(false);
            setCrtCanDrive(false);
            setCrtDisabledReason(err.message);
          }
        },
        onGlowColor: (glow) => {
          // Warm white glow controlled by overall brightness — not color.
          // The CRT tube scatters light through the glass; the glow is
          // the phosphor's aggregate luminance, not individual RGB values.
          const lum = (glow.avg[0] * 0.299 + glow.avg[1] * 0.587 + glow.avg[2] * 0.114) / 255;
          const a = Math.min(lum * 0.35, 0.3); // opacity scales with brightness
          setGlowStyle({
            boxShadow: [
              `0 0 40px 10px rgba(220,210,190,${a.toFixed(3)})`,
              `0 0 80px 25px rgba(220,210,190,${(a * 0.5).toFixed(3)})`,
            ].join(", "),
            transition: "box-shadow 400ms ease",
          });
        },
      });
      shaderHandleRef.current = handle;
      setCrtMounted(true);
      setCrtPrefs((p) => ({ ...p, crtEnabled: true, selectedLookId: look.id }));
    } catch (err) {
      console.error("[crt] mount failed:", err);
    } finally {
      setCrtLoading(false);
    }
  }, [crtMounted, crtPrefs]);

  // Unmount the shader layer when the queue empties (demo channel returns).
  useEffect(() => {
    if (!current && crtMounted) {
      shaderHandleRef.current?.unmount();
      shaderHandleRef.current = null;
      setCrtMounted(false);
    }
  }, [current, crtMounted]);

  // ----- MediaSession wiring ----------------------------------------------

  useEffect(() => {
    setMediaSessionMetadata(current);
  }, [current]);

  useEffect(() => {
    setMediaSessionHandlers({
      play: () => playerRef.current?.play(),
      pause: () => playerRef.current?.pause(),
      next: doNext,
      prev: doPrev,
      seekBy: (s) => playerRef.current?.seekRelative(s),
    });
  }, [doNext, doPrev]);

  // ----- global keyboard ---------------------------------------------------

  useEffect(() => {
    return installKeyboardHandlers({
      togglePlay: () => playerRef.current?.togglePlay(),
      nextItem: doNext,
      prevItem: doPrev,
      seekRelative: (s) => playerRef.current?.seekRelative(s),
      toggleFullscreen: () => playerRef.current?.toggleFullscreen(),
      togglePiP: () => playerRef.current?.togglePiP(),
      toggleMute: () => playerRef.current?.toggleMute(),
      toggleShortcutsOverlay: () => setShortcutsOpen((open) => !open),
      closeOverlay: () => setShortcutsOpen(false),
    });
  }, [doNext, doPrev]);

  // ----- render ------------------------------------------------------------

  return (
    <div className="app">
      <header className="app-header">
        <h1>Night Light</h1>
        <span className="tagline">your personal archive.org channel</span>
      </header>

      <main className="app-main">
        <section>
          <PasteBox onSubmit={addByUrl} disabled={loading} />
          {loading && <p className="notice">Loading metadata…</p>}
          {notice && <p className={`notice ${notice.kind}`}>{notice.message}</p>}
          {showDemo && (
            <EmptyState
              onLoaded={(items) =>
                setQueue((q) => {
                  // If the visitor pasted something while the demo was
                  // still loading, keep their paste — don't stomp.
                  if (q.items.length > 0) return q;
                  return { name: DEMO_NAME, items, currentIndex: 0 };
                })
              }
            />
          )}
          {current && (
            <>
              <Player
                ref={playerRef}
                item={current}
                isDemo={showDemo || !settings.userOwnsQueue}
                onEnded={doNext}
                onShowShortcuts={() => setShortcutsOpen(true)}
                crtActive={crtMounted}
                crtDisabled={!crtCanDrive}
                crtDisabledTooltip={crtDisabledReason ?? undefined}
                crtLoading={crtLoading}
                onCrtToggle={handleCrtToggle}
                canvasRef={canvasRef}
                crtGlowStyle={glowStyle}
              />
              <Attribution item={current} />
            </>
          )}
        </section>
        <aside>
          <QueueList
            queue={queue}
            onJumpTo={doJumpTo}
            onRemove={doRemove}
            onReorder={doReorder}
          />
        </aside>
      </main>

      <DonateFooter />

      <ShortcutsOverlay open={shortcutsOpen} onClose={() => setShortcutsOpen(false)} />
    </div>
  );
}
